import React, { useEffect, useState, Suspense, useCallback } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useAuth } from "@/hooks/useAuth";
import { useFullscreen } from "@/hooks/useFullscreen";
import "@/i18n"; // Initialize i18next
import { useDispatch, useSelector } from "react-redux";
import { setCurrentOutletId } from "@/store/slices/settingsSlice";
import { selectStoreAndOutlet } from "@/store/slices/storeSlice";
import { settingsApi } from "@/store/api/settingsApi";

// Components
import LoginPage from "@/pages/LoginPage";
import MainLayout from "@/components/common/MainLayout";
import ProtectedRoute from "@/components/common/ProtectedRoute";
import PasswordExitDialog from "@/components/common/PasswordExitDialog";
import FullscreenPrompt from "@/components/common/FullscreenPrompt";
import { SubscriptionExpiredModal } from "@/components/subscription/SubscriptionExpiredModal";
import { useSubscriptionGuard } from "@/hooks/useSubscriptionGuard";
import { CookieConsentBanner } from "@/components/common/CookieConsentBanner";
import { TimezoneMismatchWarning } from "@/components/common/TimezoneMismatchWarning";
import { FullscreenProvider } from "@/contexts/FullscreenContext";
import { ZoomProvider } from "@/contexts/ZoomContext";
import ResponsiveThemeProvider from "@/styles/ResponsiveThemeProvider";

// Customer display — public read-only page (no auth, no layout)
const CustomerDisplay = React.lazy(() => import("@/pages/CustomerDisplay"));

// Lazy load pages for better performance
const LandingPage = React.lazy(() => import("@/pages/LandingPage"));
const CompanyProfilePage = React.lazy(() => import("@/pages/CompanyProfilePage"));
const PrivacyPolicyPage = React.lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsConditionsPage = React.lazy(() => import("@/pages/TermsConditionsPage"));
const ContactUsPage = React.lazy(() => import("@/pages/ContactUsPage"));
const Dashboard = React.lazy(() => import("@/pages/Dashboard"));
const POS = React.lazy(() => import("@/pages/POS"));
const Inventory = React.lazy(() => import("@/pages/Inventory"));
const Customers = React.lazy(() => import("@/pages/Customers"));
const Transactions = React.lazy(() => import("@/pages/Transactions"));
const Reports = React.lazy(() => import("@/pages/Reports"));
const MultiOutletDashboard = React.lazy(() => import("@/pages/MultiOutletDashboard"));
const OutletComparisonReport = React.lazy(() => import("@/pages/OutletComparisonReport"));
const StoreFinancialReport = React.lazy(() => import("@/pages/StoreFinancialReport"));
const Expenses = React.lazy(() => import("@/pages/Expenses"));
const Settings = React.lazy(() => import("@/pages/Settings"));
const Users = React.lazy(() => import("@/pages/Users"));
const AuditLogs = React.lazy(() => import("@/pages/AuditLogs"));
const AuditLogsSimple = React.lazy(() => import("@/pages/AuditLogsSimple"));
const OutletCashManagement = React.lazy(() => import("@/pages/OutletCashManagement"));

const StockTransfer = React.lazy(() => import("@/pages/StockTransfer"));
const StockAudit = React.lazy(() => import("@/pages/StockAudit"));
const StockAuditDetail = React.lazy(() => import("@/pages/StockAuditDetail"));
const PurchaseOrders = React.lazy(() => import("@/pages/PurchaseOrders"));
const Promotions = React.lazy(() => import("@/pages/Promotions"));

// Loading component
const PageLoader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
    <CircularProgress />
  </Box>
);

function App() {
  const location = useLocation();
  const { isAuthenticated, verify, user, logout } = useAuth();
  const { isFullscreen, enterFullscreen, exitFullscreen } = useFullscreen();
  const dispatch = useDispatch();
  const { showExpiredModal, setShowExpiredModal } = useSubscriptionGuard();

  // Use storeSlice as source of truth for current store/outlet
  const { storeId, outletId } = useSelector(selectStoreAndOutlet);

  const [initializing, setInitializing] = useState(true);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [pendingExit, setPendingExit] = useState(false);
  const [showFullscreenPrompt, setShowFullscreenPrompt] = useState(false);
  const [blockingExit, setBlockingExit] = useState(false);
  const [authorizedExit, setAuthorizedExit] = useState(false);

  // Toggle this to re-enable fullscreen enforcement
  const FULLSCREEN_ENABLED = false;

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem("token");

      if (token && !user) {
        try {
          await verify();
        } catch (error) {
          console.error("Token verification failed:", error);
          // Token verification failure is handled in the auth slice
        }
      }

      setInitializing(false);
    };

    initializeAuth();
  }, [verify, user]);

  // Initialize outlet settings when user is authenticated
  // Uses storeSlice (storeId/outletId) as source of truth
  useEffect(() => {
    if (isAuthenticated && user && storeId && outletId) {
      console.log("Initializing settings for store:", storeId, "outlet:", outletId);

      // Set current outlet in Redux settings slice
      dispatch(setCurrentOutletId(outletId));

      // Fetch system settings (includes outlet-specific settings)
      dispatch(
        settingsApi.endpoints.getSystemSettings.initiate({ storeId, outletId }) as any
      );
    }
  }, [isAuthenticated, user, storeId, outletId, dispatch]);

  // Show fullscreen prompt when authenticated and not in fullscreen (but only if not authorized to be out)
  useEffect(() => {
    if (!FULLSCREEN_ENABLED) return;
    if (isAuthenticated && !initializing && !isFullscreen && !authorizedExit) {
      setShowFullscreenPrompt(true);
    } else {
      setShowFullscreenPrompt(false);
    }
  }, [isAuthenticated, initializing, isFullscreen, authorizedExit]);

  // Additional protection using visibility change and focus events
  useEffect(() => {
    if (!FULLSCREEN_ENABLED) return;
    if (!isAuthenticated) return;

    const handleVisibilityChange = () => {
      // If page becomes hidden while in fullscreen, user might be trying to exit
      if (document.hidden && isFullscreen && !pendingExit) {
        setTimeout(() => {
          if (!document.fullscreenElement && !showPasswordDialog) {
            setShowPasswordDialog(true);
          }
        }, 100);
      }
    };

    const handleWindowBlur = () => {
      // Check if we lost fullscreen when window lost focus
      setTimeout(() => {
        if (isFullscreen && !document.fullscreenElement && !pendingExit && !showPasswordDialog) {
          setShowPasswordDialog(true);
        }
      }, 100);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, [isAuthenticated, isFullscreen, pendingExit, showPasswordDialog]);

  // Monitor for mouse and touch gestures that might exit fullscreen
  useEffect(() => {
    if (!isAuthenticated || !isFullscreen) return;

    let mouseStartY = 0;
    let touchStartY = 0;

    const handleMouseDown = (event: MouseEvent) => {
      mouseStartY = event.clientY;
    };

    const handleMouseMove = (event: MouseEvent) => {
      // Detect upward mouse movement near top of screen (might be trying to access browser controls)
      if (event.clientY < 5 && mouseStartY > 50) {
        console.log("Detected suspicious mouse movement toward browser controls");
        // Don't block immediately, but monitor for fullscreen exit
      }
    };

    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length > 0) {
        touchStartY = event.touches[0].clientY;
      }
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches.length > 0) {
        const currentY = event.touches[0].clientY;
        // Detect swipe down from top (common mobile gesture to exit fullscreen)
        if (touchStartY < 20 && currentY > touchStartY + 100) {
          console.log("Detected swipe down from top");
          if (!showPasswordDialog) {
            setShowPasswordDialog(true);
          }
        }
      }
    };

    // Add mouse and touch listeners
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("touchstart", handleTouchStart, { passive: false });
    document.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
    };
  }, [isAuthenticated, isFullscreen, showPasswordDialog]);

  // Monitor for navigation and tab management attempts
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isFullscreen) {
        console.log("Detected attempt to leave page while in fullscreen");
        event.preventDefault();
        event.returnValue = ""; // Some browsers require this
        if (!showPasswordDialog) {
          setShowPasswordDialog(true);
        }
        return "";
      }
    };

    const handlePopstate = (event: PopStateEvent) => {
      if (isFullscreen) {
        console.log("Detected browser back/forward navigation while in fullscreen");
        event.preventDefault();
        if (!showPasswordDialog) {
          setShowPasswordDialog(true);
        }
      }
    };

    const handleFocusOut = () => {
      if (isFullscreen) {
        console.log("Window lost focus while in fullscreen");
        // Check if fullscreen is still active after a brief delay
        setTimeout(() => {
          if (!document.fullscreenElement && !pendingExit && !showPasswordDialog) {
            setShowPasswordDialog(true);
          }
        }, 500);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopstate);
    window.addEventListener("focusout", handleFocusOut);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopstate);
      window.removeEventListener("focusout", handleFocusOut);
    };
  }, [isAuthenticated, isFullscreen, pendingExit, showPasswordDialog]);

  // Aggressive fullscreen enforcement - continuous monitoring
  useEffect(() => {
    if (!FULLSCREEN_ENABLED) return;
    if (!isAuthenticated) return;

    const enforceFullscreen = () => {
      const currentlyFullscreen = !!document.fullscreenElement;

      // If we should be fullscreen but we're not, and it's not an authorized exit
      if (
        isAuthenticated &&
        !currentlyFullscreen &&
        !pendingExit &&
        !blockingExit &&
        !authorizedExit
      ) {
        console.log("FULLSCREEN VIOLATION DETECTED - Enforcing immediately");
        setBlockingExit(true);
        setShowPasswordDialog(true);

        // Force re-entry
        enterFullscreen().catch(err => {
          console.error("Failed to re-enter fullscreen:", err);
          // If we can't re-enter, keep trying (only if still not authorized)
          setTimeout(() => {
            if (!pendingExit && !authorizedExit && !document.fullscreenElement) {
              enterFullscreen().catch(console.error);
            }
          }, 500);
        });
      }
    };

    // Check every 250ms for maximum responsiveness
    const interval = setInterval(enforceFullscreen, 250);
    return () => clearInterval(interval);
  }, [
    isAuthenticated,
    isFullscreen,
    pendingExit,
    blockingExit,
    authorizedExit,
    showPasswordDialog,
    enterFullscreen,
  ]);

  // Handle fullscreen toggle - always show password for exit
  const handleFullscreenToggle = useCallback(async () => {
    if (isFullscreen) {
      // If currently fullscreen, show password dialog to exit
      setShowPasswordDialog(true);
    } else {
      // If not fullscreen, enter fullscreen directly and reset authorized exit
      console.log("User manually entering fullscreen - resetting authorization");
      setAuthorizedExit(false);
      await enterFullscreen();
    }
  }, [isFullscreen, enterFullscreen]);

  // Handle fullscreen exit attempts with aggressive re-entry
  useEffect(() => {
    if (!FULLSCREEN_ENABLED) return;
    const handleFullscreenChange = () => {
      console.log("Fullscreen change detected:", {
        authenticated: isAuthenticated,
        fullscreenElement: !!document.fullscreenElement,
        pendingExit,
        blockingExit,
        showPasswordDialog,
      });

      // If user exits fullscreen without permission, immediately block and show password dialog
      if (
        isAuthenticated &&
        !document.fullscreenElement &&
        !pendingExit &&
        !blockingExit &&
        !authorizedExit
      ) {
        console.log("UNAUTHORIZED EXIT DETECTED - Blocking immediately");
        setBlockingExit(true);
        setShowPasswordDialog(true);

        // Immediately try to re-enter fullscreen
        setTimeout(() => {
          if (!pendingExit && !authorizedExit) {
            console.log("Re-entering fullscreen after unauthorized exit");
            enterFullscreen().catch(console.error);
          }
        }, 100);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isAuthenticated) return;

      console.log("Key pressed:", event.key, "Fullscreen:", isFullscreen);

      // Ctrl+F to toggle fullscreen (requires password for exit)
      if (event.ctrlKey && event.key === "f") {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        handleFullscreenToggle();
        return;
      }

      // Block ALL keys that might exit fullscreen
      if (event.key === "F11" || event.key === "Escape") {
        console.log("BLOCKING KEY:", event.key);
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        // Always show password dialog for these keys
        if (!showPasswordDialog && !blockingExit) {
          console.log("Showing password dialog for:", event.key);
          setBlockingExit(true);
          setShowPasswordDialog(true);
        }
        return false;
      }
    };

    // Add event listeners for fullscreen changes
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    // Add aggressive keyboard blocking at multiple levels
    document.addEventListener("keydown", handleKeyDown, { capture: true, passive: false });
    document.addEventListener("keyup", handleKeyDown, { capture: true, passive: false });
    document.addEventListener("keypress", handleKeyDown, { capture: true, passive: false });
    window.addEventListener("keydown", handleKeyDown, { capture: true, passive: false });
    window.addEventListener("keyup", handleKeyDown, { capture: true, passive: false });

    // Block right-click context menu
    const blockContextMenu = (e: Event) => {
      if (isFullscreen) {
        e.preventDefault();
        return false;
      }
    };
    document.addEventListener("contextmenu", blockContextMenu, { capture: true, passive: false });

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
      document.removeEventListener("keyup", handleKeyDown, { capture: true });
      document.removeEventListener("keypress", handleKeyDown, { capture: true });
      window.removeEventListener("keydown", handleKeyDown, { capture: true });
      window.removeEventListener("keyup", handleKeyDown, { capture: true });
      document.removeEventListener("contextmenu", blockContextMenu, { capture: true });
    };
  }, [isAuthenticated, pendingExit, showPasswordDialog, isFullscreen, handleFullscreenToggle]);

  // Handle password dialog confirmation
  const handlePasswordConfirm = useCallback(async () => {
    console.log("Password confirmed - allowing permanent exit");
    setAuthorizedExit(true);
    setPendingExit(true);
    setBlockingExit(false);
    setShowPasswordDialog(false);

    try {
      await exitFullscreen();
      console.log("Successfully exited fullscreen");
    } catch (error) {
      console.error("Failed to exit fullscreen:", error);
    }

    // Reset states after a delay
    setTimeout(() => {
      setPendingExit(false);
      setBlockingExit(false);
      console.log("Exit completed - user can stay out of fullscreen");
    }, 2000);
  }, [exitFullscreen]);

  // Handle password dialog cancellation
  const handlePasswordCancel = useCallback(async () => {
    console.log("Password dialog cancelled - forcing fullscreen");
    setShowPasswordDialog(false);
    setBlockingExit(false);
    setAuthorizedExit(false); // Reset authorization on cancel
    // Force re-enter fullscreen if user cancels
    setTimeout(async () => {
      if (!isFullscreen && !pendingExit) {
        await enterFullscreen();
      }
    }, 100);
  }, [isFullscreen, enterFullscreen, pendingExit]);

  // Customer display — render without auth check, layout, or fullscreen enforcement
  if (location.pathname === '/customer-display') {
    return (
      <Suspense fallback={<Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ bgcolor: '#0f172a' }} />}>
        <CustomerDisplay />
      </Suspense>
    );
  }

  // Show loading spinner only while initializing (not during login attempts)
  if (initializing) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Separate public and authenticated routes
  return (
    <>
      {!isAuthenticated ? (
        // Public routes - no MainLayout, no fullscreen enforcement
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/company-profile" element={<CompanyProfilePage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-conditions" element={<TermsConditionsPage />} />
            <Route path="/contact-us" element={<ContactUsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      ) : (
        // Authenticated routes - with MainLayout and fullscreen enforcement
        <ZoomProvider>
          <ResponsiveThemeProvider>
            <FullscreenProvider handleFullscreenToggle={handleFullscreenToggle}>
              {/* Fullscreen prompt */}
              {showFullscreenPrompt && (
                <FullscreenPrompt
                  onEnterFullscreen={handleFullscreenToggle}
                  onDismiss={() => setShowFullscreenPrompt(false)}
                />
              )}

              <MainLayout>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Redirect root to dashboard for authenticated users */}
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />

                    {/* Dashboard */}
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />

                    {/* Point of Sale */}
                    <Route
                      path="/pos"
                      element={
                        <ProtectedRoute permissions={["pos.create_transaction"]}>
                          <POS />
                        </ProtectedRoute>
                      }
                    />

                    {/* Inventory Management */}
                    <Route
                      path="/inventory"
                      element={
                        <ProtectedRoute permissions={["inventory.view"]}>
                          <Inventory />
                        </ProtectedRoute>
                      }
                    />

                    {/* Stock Transfer */}
                    <Route
                      path="/inventory/stock-transfer"
                      element={
                        <ProtectedRoute permissions={["inventory.transfer_stock"]}>
                          <StockTransfer />
                        </ProtectedRoute>
                      }
                    />

                    {/* Stock Audit */}
                    <Route
                      path="/stock-audit"
                      element={
                        <ProtectedRoute permissions={["stock_audit.view"]}>
                          <StockAudit />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/stock-audit/:id"
                      element={
                        <ProtectedRoute permissions={["stock_audit.view"]}>
                          <StockAuditDetail />
                        </ProtectedRoute>
                      }
                    />

                    {/* Purchase Orders */}
                    <Route
                      path="/purchase-orders"
                      element={
                        <ProtectedRoute permissions={["purchase_orders.view"]}>
                          <PurchaseOrders />
                        </ProtectedRoute>
                      }
                    />

                    {/* Promotions */}
                    <Route
                      path="/promotions"
                      element={
                        <ProtectedRoute permissions={["promotions.view"]}>
                          <Promotions />
                        </ProtectedRoute>
                      }
                    />

                    {/* Customer Management */}
                    <Route
                      path="/customers"
                      element={
                        <ProtectedRoute permissions={["customers.view"]}>
                          <Customers />
                        </ProtectedRoute>
                      }
                    />

                    {/* Transactions */}
                    <Route
                      path="/transactions"
                      element={
                        <ProtectedRoute permissions={["pos.view_transaction"]}>
                          <Transactions />
                        </ProtectedRoute>
                      }
                    />

                    {/* Reports */}
                    <Route
                      path="/reports"
                      element={
                        <ProtectedRoute
                          permissions={[
                            "reports.view_sales",
                            "reports.view_inventory",
                            "reports.view_customers",
                            "reports.view_financial",
                            "stock_audit.view",
                          ]}
                        >
                          <Reports />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/reports/multi-outlet"
                      element={
                        <ProtectedRoute permissions={["reports.view_sales"]} requireRole="MANAGER">
                          <MultiOutletDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/reports/outlet-comparison"
                      element={
                        <ProtectedRoute permissions={["reports.view_sales"]} requireRole="MANAGER">
                          <OutletComparisonReport />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/reports/store-financial"
                      element={
                        <ProtectedRoute permissions={["reports.view_financial"]}>
                          <StoreFinancialReport />
                        </ProtectedRoute>
                      }
                    />

                    {/* Expenses */}
                    <Route
                      path="/expenses"
                      element={
                        <ProtectedRoute permissions={["expenses.view"]}>
                          <Expenses />
                        </ProtectedRoute>
                      }
                    />

                    {/* Outlet Cash Management */}
                    <Route
                      path="/outlet-cash"
                      element={
                        <ProtectedRoute permissions={["cash.view_outlet_cash"]}>
                          <OutletCashManagement />
                        </ProtectedRoute>
                      }
                    />

                    {/* Settings */}
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute
                          permissions={[
                            "system.manage_settings",
                            "system.view_store",
                            "system.manage_backup",
                          ]}
                        >
                          <Settings />
                        </ProtectedRoute>
                      }
                    />

                    {/* User Management */}
                    <Route
                      path="/users"
                      element={
                        <ProtectedRoute permissions={["users.view"]}>
                          <Users />
                        </ProtectedRoute>
                      }
                    />

                    {/* Audit Logs */}
                    <Route
                      path="/audit"
                      element={
                        <ProtectedRoute permissions={["audit.view_logs"]}>
                          <AuditLogs />
                        </ProtectedRoute>
                      }
                    />

                    {/* Simple Audit Logs for debugging */}
                    <Route
                      path="/audit-simple"
                      element={
                        <ProtectedRoute permissions={["audit.view_logs"]}>
                          <AuditLogsSimple />
                        </ProtectedRoute>
                      }
                    />

                    {/* Default redirect */}
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </Suspense>
              </MainLayout>
            </FullscreenProvider>
          </ResponsiveThemeProvider>
          {/* Password dialog for fullscreen exit */}
          <PasswordExitDialog
            open={showPasswordDialog}
            onClose={handlePasswordCancel}
            onConfirm={handlePasswordConfirm}
          />
          {/* Subscription expired modal */}
          <SubscriptionExpiredModal
            open={showExpiredModal}
            stores={user?.stores || []}
            currentStoreId={storeId}
            onLogout={() => {
              setShowExpiredModal(false);
              logout();
            }}
            onSwitchStore={async (switchToStoreId) => {
              setShowExpiredModal(false);
              const { switchStore: switchStoreFn } = await import('@/store/slices/authSlice');
              dispatch(switchStoreFn(switchToStoreId) as any);
            }}
          />
        </ZoomProvider>
      )}
      <CookieConsentBanner />
      <TimezoneMismatchWarning />
    </>
  );
}

export default App;
